// AnchorValueTour — interactive new-agent product tour.
//
// Goal: in ~2 minutes, show a brand-new agent EXACTLY how the
// system fixes the three biggest time/money sinks in their book:
//   1. Manual issue follow-up (ANCHOR closes it for them)
//   2. Missed renewals + dropped lifecycle touchpoints (workflow engine)
//   3. Cross-sell gaps they never get back to (coverage scanner)
//
// Each step is INTERACTIVE — they pick, type, toggle, watch
// animated simulations of real flows — not a passive video.
//
// Shown on first sign-in (gated by localStorage flag). Can be
// replayed from Settings → "Replay value tour".
//
// Self-contained — no third-party tour dep. Tailwind + small
// React state machines for animations.

import React from 'react';
import { supabase } from '../lib/supabase';

const STORAGE_KEY = 'crm:value_tour:v1';

export function shouldShowValueTour() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v !== 'completed' && v !== 'dismissed';
  } catch { return true; }
}

export function markValueTourCompleted() {
  try { localStorage.setItem(STORAGE_KEY, 'completed'); } catch {}
}

export function markValueTourDismissed() {
  try { localStorage.setItem(STORAGE_KEY, 'dismissed'); } catch {}
}

// ── Cross-sell revenue estimates (mirrors api/anchor/issues-list) ─
const REV_PER_COVERAGE_PER_YR = {
  dental: 504, vision: 180, life: 360, final_expense: 540,
  accident: 216, hospital_indemnity: 480, critical_illness: 360,
  cancer: 264, disability: 540,
};
const COVERAGE_OPTIONS = [
  { key: 'dental',             label: 'Dental',             icon: '🦷' },
  { key: 'vision',             label: 'Vision',             icon: '👁️' },
  { key: 'life',               label: 'Life',               icon: '🛡️' },
  { key: 'accident',           label: 'Accident',           icon: '🩹' },
  { key: 'hospital_indemnity', label: 'Hospital indemnity', icon: '🏥' },
  { key: 'critical_illness',   label: 'Critical illness',   icon: '⚕️' },
];

// ── Step constants ────────────────────────────────────────────
const TOTAL_STEPS = 7;

// ── Typewriter helper — reveal text char-by-char ──────────────
function useTypewriter(fullText, on, speedMs = 18) {
  const [shown, setShown] = React.useState('');
  React.useEffect(() => {
    if (!on) { setShown(''); return; }
    setShown('');
    let i = 0;
    const id = setInterval(() => {
      i++;
      setShown(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(id);
    }, speedMs);
    return () => clearInterval(id);
  }, [on, fullText, speedMs]);
  return shown;
}

// ── Sub-component: SMS bubble with typewriter ────────────────
function SmsBubble({ from, body, time, visible }) {
  const typed = useTypewriter(body, visible);
  if (!visible) return null;
  return (
    <div className="flex flex-col items-end mb-2 animate-fade-in">
      <div className="bg-emerald-500 text-white px-4 py-2 rounded-2xl rounded-br-md max-w-[80%] shadow-md">
        <div className="text-xs text-emerald-100 mb-0.5">{from}</div>
        <div className="text-sm">{typed}<span className="inline-block w-1 animate-pulse">|</span></div>
      </div>
      <div className="text-[11px] text-slate-400 mt-0.5 mr-1">{time}</div>
    </div>
  );
}

// ── Sub-component: Progress dots ─────────────────────────────
function ProgressDots({ step }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const filled = i + 1 <= step;
        const current = i + 1 === step;
        return (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              current ? 'w-6 bg-violet-500' : filled ? 'w-1.5 bg-violet-300' : 'w-1.5 bg-slate-200'
            }`}
          />
        );
      })}
    </div>
  );
}

// ── Step 1: Welcome ──────────────────────────────────────────
function Step1Welcome({ firstName, setFirstName, niche, setNiche, onNext }) {
  // Three niche choices drive the rest of the tour. We default-select
  // none so the operator has to make an active pick (no accidental
  // "Show me a flow for a niche I don't sell" misfire).
  const NICHE_OPTIONS = [
    { key: 'health',   label: 'Health (ACA / PPO)', emoji: '🩺', sub: 'Marketplace + private insurance' },
    { key: 'medicare', label: 'Medicare',           emoji: '🪪', sub: 'Advantage · Medigap · Part D' },
    { key: 'life',     label: 'Life Insurance',     emoji: '🛡️', sub: 'Term · Whole · IUL · Final Expense' },
  ];
  const ready = firstName.trim() && niche;
  return (
    <div className="text-center px-6 py-8">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 text-3xl mb-4">👋</div>
      <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome to Six Figure Agent</h2>
      <p className="text-slate-600 text-lg mb-6">Most CRMs make you set up for hours before you see value.<br/>We're going to show you what we do for you in 90 seconds.</p>
      <div className="max-w-md mx-auto text-left">
        <label className="block text-sm font-medium text-slate-700 mb-1">What's your first name?</label>
        <input
          type="text"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          placeholder="e.g. Harrison"
          autoFocus
          className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg text-lg focus:border-violet-500 focus:outline-none"
        />
        <label className="block text-sm font-medium text-slate-700 mt-4 mb-2">What do you primarily sell?</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {NICHE_OPTIONS.map(opt => {
            const selected = niche === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setNiche(opt.key)}
                className={`px-3 py-2.5 rounded-lg border-2 text-left transition-all ${
                  selected
                    ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{opt.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold ${selected ? 'text-violet-900' : 'text-slate-900'}`}>{opt.label}</div>
                    <div className="text-[11px] text-slate-500">{opt.sub}</div>
                  </div>
                  {selected && <span className="text-violet-600 text-sm font-bold">✓</span>}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2 italic">We'll tailor the demos to your line. The pain + total value at the end span all three.</p>
        <button
          disabled={!ready}
          onClick={onNext}
          className="w-full mt-4 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white font-bold text-lg py-3 rounded-lg transition-colors"
        >
          Show me →
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Pain reveal ──────────────────────────────────────
// Three pain cards span the operator's whole book — ACA, Medicare,
// and Life — because most of our customers sell two or three lines.
// Each card lists examples per line so a Life-only agent still
// sees themselves in the pain, not just an ACA agent.
function Step2Pain({ firstName, onNext }) {
  return (
    <div className="px-6 py-6">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">{firstName}, here's what's eating your day right now</h2>
      <p className="text-slate-600 mb-5">Industry numbers for a multi-line agent with 200 clients across ACA, Medicare, and Life.</p>
      <div className="space-y-3">
        <PainCard
          icon="📞"
          color="amber"
          headline="47 follow-ups per month across your book"
          detail={
            <>
              ~15-20 min each across all three lines. 12-16 hrs/mo of paperwork chasing:
              <ul className="mt-1 ml-4 list-disc text-xs space-y-0.5">
                <li><strong>ACA:</strong> income docs, identity verification, AOR transfers, payment failures</li>
                <li><strong>Medicare:</strong> SOA gates, AEP outreach, ANOC delivery, OTC + dental + vision benefit activation</li>
                <li><strong>Life:</strong> policy delivery confirmation, first-draft reminders, beneficiary review</li>
              </ul>
            </>
          }
          cost="≈ 14 hrs/mo"
        />
        <PainCard
          icon="📉"
          color="red"
          headline="8-12% of your book lapses every year"
          detail={
            <>
              Missed touchpoints + skipped renewals = lost commissions. On a 200-client book:
              <ul className="mt-1 ml-4 list-disc text-xs space-y-0.5">
                <li><strong>ACA:</strong> missed OEP re-shop · grace-period payment failures · no 30-day check-in</li>
                <li><strong>Medicare:</strong> AEP plan switches happening at other agencies · no ANOC review</li>
                <li><strong>Life:</strong> NSF chargebacks · first-year persistency drops · forgotten beneficiary refreshes</li>
              </ul>
              ~16 clients × $1,200/yr commission = ~$19,200/yr lost.
            </>
          }
          cost="≈ $19,200/yr"
        />
        <PainCard
          icon="💸"
          color="emerald"
          headline="$24,000/yr of cross-sell never pitched"
          detail={
            <>
              80% of your clients have a coverage gap you'd close 1-in-4 if you actually pitched it:
              <ul className="mt-1 ml-4 list-disc text-xs space-y-0.5">
                <li><strong>ACA → upsell:</strong> dental, vision, life, accident, hospital indemnity</li>
                <li><strong>Medicare → upsell:</strong> Medicare Supplement, Part D switch, final expense, hospital indemnity</li>
                <li><strong>Life → upsell:</strong> spouse policy, IUL upgrade, ACA Marketplace for under-65 family, final expense</li>
              </ul>
            </>
          }
          cost="≈ $24,000/yr"
        />
      </div>
      <button
        onClick={onNext}
        className="w-full mt-6 bg-slate-900 hover:bg-slate-800 text-white font-bold text-lg py-3 rounded-lg"
      >
        Show me how you fix the first one →
      </button>
    </div>
  );
}

function PainCard({ icon, color, headline, detail, cost }) {
  const colorMap = {
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   fg: 'text-amber-900',   pill: 'bg-amber-100   text-amber-800' },
    red:     { bg: 'bg-red-50',     border: 'border-red-200',     fg: 'text-red-900',     pill: 'bg-red-100     text-red-800' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', fg: 'text-emerald-900', pill: 'bg-emerald-100 text-emerald-800' },
  };
  const c = colorMap[color] || colorMap.amber;
  return (
    <div className={`${c.bg} ${c.border} border rounded-lg p-4 flex items-start gap-3`}>
      <div className="text-3xl">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className={`font-bold ${c.fg}`}>{headline}</h3>
          <span className={`${c.pill} text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap`}>{cost}</span>
        </div>
        <div className={`text-sm ${c.fg} opacity-90`}>{detail}</div>
      </div>
    </div>
  );
}

// ── Step 3: Issue auto-resolution demo (niche-aware) ─────────
// Three scripts — one per niche. Health uses ANCHOR closing a real
// Marketplace income-verification request. Medicare uses an ANOC
// arrival triggering a plan-check call. Life uses a returned-draft
// (NSF) recovery — the most cash-impactful Life issue.
const DEMO_SCRIPTS = {
  health: {
    agentName:   'ANCHOR',
    agentTag:    '⚓ ANCHOR',
    headline:    'Watch ANCHOR close an ACA issue while you do something else',
    banner:      '⚓ ANCHOR is your ACA retention agent. Your Medicare + Life books get the same hands-off treatment from the Workflow Engine.',
    noticeSource: 'HealthSherpa notice imported',
    noticeColor:  'amber',
    clientName:  'Maria Lopez',
    issueTitle:  'Maria Lopez — Income verification required',
    issueMeta:   'Aetna · Deadline: 14 days · Source: CSV import',
    day0: (firstName) => `Hi Maria, this is ${firstName} — your Aetna plan has an income verification request from the Marketplace. Deadline is 2 weeks. Reply YES and I’ll walk you through exactly what they need.`,
    day1: `Hey Maria — quick follow-up on the income verification. 13 days left. Easiest path: text me your most recent pay stub photo and I’ll handle the upload.`,
    clientReply: `"Oh shoot, I forgot. Sending now."`,
    solvedTitle: 'Issue solved · 1 day 8 hours',
    solvedDetail: '2 SMS sent · 1 reply received · audit trail logged (hash-chained, immutable) · revenue protected: $1,440/yr commission',
  },
  medicare: {
    agentName:   'Workflow Engine',
    agentTag:    '🪪 Workflow Engine',
    headline:    'Watch the Workflow Engine close a Medicare issue while you do something else',
    banner:      '🪪 The Workflow Engine handles Medicare lifecycle — ANOC review, AEP outreach, SOA gating. Your ACA book gets the same hands-off treatment from ANCHOR.',
    noticeSource: 'ANOC arrival detected',
    noticeColor:  'sky',
    clientName:  'Robert Daniels',
    issueTitle:  'Robert Daniels — 2027 ANOC arrived · plan benefits changed',
    issueMeta:   'Humana Medicare Advantage · review window closes Dec 7',
    day0: (firstName) => `Hi Robert, this is ${firstName} — your 2027 Annual Notice of Change just hit your mailbox from Humana. Some benefits are shifting. Want me to review it free and tell you stay or switch? Reply YES.`,
    day1: `Robert — circling back on the Humana ANOC. AEP closes Dec 7 so I want to lock your plan choice before then. Reply YES and I’ll send you 3 alternatives matched to your meds + doctors.`,
    clientReply: `"Yes please — got the packet but it’s overwhelming."`,
    solvedTitle: 'Booked review · 18 hours',
    solvedDetail: '2 SMS sent · 1 review call booked · TPMO disclosure logged · SOA scheduled · revenue protected: ~$540/yr renewals',
  },
  life: {
    agentName:   'Workflow Engine',
    agentTag:    '🛡️ Workflow Engine',
    headline:    'Watch the Workflow Engine catch a Life policy lapse before it happens',
    banner:      '🛡️ The Workflow Engine handles Life lifecycle — first draft, beneficiary review, NSF recovery, annual refresh. Your ACA book gets the same hands-off treatment from ANCHOR.',
    noticeSource: 'Carrier statement — returned draft',
    noticeColor:  'red',
    clientName:  'David Cherryholmes',
    issueTitle:  'David Cherryholmes — Premium draft returned NSF',
    issueMeta:   'Transamerica Final Expense · $250/mo · grace window: 30 days',
    day0: (firstName) => `Hey David, this is ${firstName} — your Transamerica draft came back NSF. Policy is in a 30-day grace window. Fix is usually 5 minutes — call me at the number you saved or reply HELP.`,
    day1: `David — wanted to make sure you saw the NSF on your Transamerica policy. We’re at day 2 of the grace window. Most common cause is a new card on file. Reply CARD if that’s it and I’ll walk you through the carrier portal.`,
    clientReply: `"Oh man — yeah, new card. Send the link."`,
    solvedTitle: 'Policy saved · 19 hours',
    solvedDetail: '2 SMS sent · 1 reply received · carrier portal link sent · payment restored · audit trail logged · revenue protected: $3,000/yr premium + $2,250 commission protected from chargeback',
  },
};

function Step3AnchorDemo({ firstName, niche, onNext }) {
  const script = DEMO_SCRIPTS[niche] || DEMO_SCRIPTS.health;
  const [stage, setStage] = React.useState(0);
  // Reset animation when the niche changes (defensive — niche is
  // locked at step 1 today, but if we ever let it change later this
  // keeps the demo from getting stuck mid-animation).
  React.useEffect(() => { setStage(0); }, [niche]);

  React.useEffect(() => {
    if (stage >= 5) return;
    const next = setTimeout(() => setStage(s => s + 1), [600, 1400, 2400, 2200, 1800, 1800][stage]);
    return () => clearTimeout(next);
  }, [stage]);

  const noticeBorderClass = ({
    amber: 'border-amber-300',
    sky:   'border-sky-300',
    red:   'border-red-300',
  })[script.noticeColor] || 'border-amber-300';
  const noticeTextClass = ({
    amber: 'text-amber-700',
    sky:   'text-sky-700',
    red:   'text-red-700',
  })[script.noticeColor] || 'text-amber-700';

  return (
    <div className="px-6 py-6">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">{script.headline}</h2>
      <p className="text-slate-600 mb-1">A real flow at real speed. We sped up the SMS animations 100x so you don't have to wait days.</p>
      <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 mb-4 inline-block">
        {script.banner}
      </p>

      <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4">
        {stage >= 1 && (
          <div className={`bg-white border ${noticeBorderClass} rounded-lg p-3 mb-3 animate-fade-in shadow-sm`}>
            <div className={`flex items-center gap-2 text-xs font-semibold ${noticeTextClass} uppercase tracking-wide`}>
              <span>🚨</span> {script.noticeSource}
            </div>
            <div className="mt-1 font-bold text-slate-900">{script.issueTitle}</div>
            <div className="text-sm text-slate-500">{script.issueMeta}</div>
          </div>
        )}

        <div className="space-y-2 min-h-[280px]">
          <SmsBubble
            visible={stage >= 2}
            from={`${script.agentTag} · You · Day 0, 2:14pm`}
            body={script.day0(firstName)}
            time="Sent ✓"
          />
          <SmsBubble
            visible={stage >= 3}
            from={`${script.agentTag} · You · Day 1, 10:08am`}
            body={script.day1}
            time="Sent ✓"
          />
          {stage >= 4 && (
            <div className="flex flex-col items-start mb-2 animate-fade-in">
              <div className="bg-slate-200 text-slate-900 px-4 py-2 rounded-2xl rounded-bl-md max-w-[80%]">
                <div className="text-xs text-slate-500 mb-0.5">{script.clientName.split(' ')[0]} · 10:14am</div>
                <div className="text-sm">{script.clientReply}</div>
              </div>
            </div>
          )}
          {stage >= 5 && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 mt-3 animate-fade-in">
              <div className="flex items-center gap-2 text-sm font-bold text-emerald-900">
                <span>✓</span> {script.solvedTitle}
              </div>
              <div className="text-xs text-emerald-800 mt-1">
                {script.solvedDetail}
              </div>
              <div className="text-xs text-emerald-800 mt-1 italic">
                Time you spent on this: 0 minutes.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center mt-5">
        <div className="text-sm text-slate-500">
          {stage < 5 ? `Watching ${script.agentName} work…` : 'Cool, right?'}
        </div>
        <button
          onClick={onNext}
          disabled={stage < 5}
          className="bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white font-bold px-5 py-2.5 rounded-lg transition-colors"
        >
          Show me #2 →
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Workflow engine demo (niche-aware) ───────────────
// Each niche shows the actual step sequence that fires for that
// LOB — pulled from the spec'd workflow defs (api/_workflowDefs.js).
// We pick the sample client + carrier per niche so the demo
// matches what the operator's book actually looks like.
const WORKFLOW_DEMOS = {
  health: {
    clientLabel: '📋 Maria Lopez — Aetna ACA Marketplace · effective today',
    steps: [
      { day: 0,   title: 'Welcome message',              channel: 'SMS + email' },
      { day: 2,   title: 'Portal registration nudge',    channel: 'SMS' },
      { day: 5,   title: 'Autopay setup reminder',       channel: 'SMS' },
      { day: 10,  title: 'ID card check',                channel: 'SMS' },
      { day: 14,  title: 'PCP selection',                channel: 'SMS' },
      { day: 30,  title: '30-day check-in',              channel: 'SMS' },
      { day: 90,  title: '90-day check-in',              channel: 'SMS' },
      { day: 180, title: 'Mid-year review',              channel: 'SMS' },
      { day: 290, title: 'OEP re-shop nudge',            channel: 'SMS' },
      { day: 330, title: 'Annual review',                channel: 'Task' },
    ],
    closer: '12 months later → autopay clean · OEP renewal locked',
  },
  medicare: {
    clientLabel: '📋 Robert Daniels — Humana Medicare Advantage · effective today',
    steps: [
      { day: 0,   title: 'Welcome + Evidence of Coverage', channel: 'SMS + email' },
      { day: 3,   title: 'Portal registration nudge',      channel: 'SMS' },
      { day: 10,  title: 'ID card check',                  channel: 'SMS' },
      { day: 14,  title: 'PCP selection',                  channel: 'SMS' },
      { day: 18,  title: 'Prescription drug walkthrough',  channel: 'SMS' },
      { day: 21,  title: 'Annual Wellness Visit',          channel: 'SMS' },
      { day: 30,  title: 'OTC + dental + vision + fitness',channel: 'SMS' },
      { day: 35,  title: '30-day check-in',                channel: 'SMS' },
      { day: 90,  title: '90-day check-in',                channel: 'SMS' },
      { day: 270, title: 'ANOC arrival',                   channel: 'SMS' },
      { day: 285, title: 'AEP outreach',                   channel: 'Task' },
      { day: 335, title: 'Annual review',                  channel: 'Task' },
    ],
    closer: '12 months later → AEP review booked + SOA logged · TPMO compliant throughout',
  },
  life: {
    clientLabel: '📋 David Cherryholmes — Transamerica Final Expense · in force today',
    steps: [
      { day: 0,   title: 'Policy issue confirmation',     channel: 'SMS + email' },
      { day: 12,  title: 'Policy delivery confirmation',  channel: 'SMS' },
      { day: 14,  title: 'Beneficiary confirmation',      channel: 'SMS' },
      { day: 20,  title: 'First premium draft heads-up',  channel: 'SMS' },
      { day: 30,  title: 'Online account registration',   channel: 'SMS' },
      { day: 30,  title: '30-day check-in',               channel: 'SMS' },
      { day: 90,  title: '90-day check-in',               channel: 'SMS' },
      { day: 120, title: 'IUL cash value education',      channel: 'Email' },
      { day: 335, title: 'Annual policy review',          channel: 'Task' },
      { day: 365, title: 'Annual beneficiary refresh',    channel: 'SMS' },
    ],
    closer: '12 months later → policy in force · beneficiaries verified · NSF risk caught early',
  },
};

function Step4WorkflowDemo({ firstName, niche, onNext }) {
  const demo = WORKFLOW_DEMOS[niche] || WORKFLOW_DEMOS.health;
  const steps = demo.steps;
  const [stepDone, setStepDone] = React.useState(0);
  React.useEffect(() => { setStepDone(0); }, [niche]);

  React.useEffect(() => {
    if (stepDone >= steps.length) return;
    const t = setTimeout(() => setStepDone(s => s + 1), 220);
    return () => clearTimeout(t);
  }, [stepDone, steps.length]);

  return (
    <div className="px-6 py-6">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Every client gets a custom retention sequence — across every line you sell</h2>
      <p className="text-slate-600 mb-2">Same engine for ACA · Medicare · Life · Dental + Vision · Supplemental. Carrier-aware messages. Hits the right step on the right day in their timezone.</p>
      <div className="flex items-center gap-2 flex-wrap text-xs mb-4">
        <span className={`px-2 py-0.5 rounded-full font-semibold ${niche === 'health'   ? 'bg-sky-200 text-sky-900 ring-2 ring-sky-400'         : 'bg-sky-100 text-sky-800'}`}>🩺 ACA: 10 steps</span>
        <span className={`px-2 py-0.5 rounded-full font-semibold ${niche === 'medicare' ? 'bg-emerald-200 text-emerald-900 ring-2 ring-emerald-400' : 'bg-emerald-100 text-emerald-800'}`}>🪪 Medicare: 12 steps</span>
        <span className={`px-2 py-0.5 rounded-full font-semibold ${niche === 'life'     ? 'bg-amber-200 text-amber-900 ring-2 ring-amber-400'   : 'bg-amber-100 text-amber-800'}`}>🛡️ Life: 10 steps</span>
      </div>

      <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4">
        <div className="text-sm font-bold text-slate-700 mb-2">{demo.clientLabel}</div>
        <div className="grid gap-2">
          {steps.map((s, i) => {
            const done = i < stepDone;
            const current = i === stepDone - 1;
            return (
              <div
                key={s.key}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${
                  done ? 'bg-emerald-50 border-emerald-300 opacity-100' :
                         'bg-white border-slate-200 opacity-40'
                } ${current ? 'ring-2 ring-violet-300 scale-[1.02]' : ''}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                  done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {done ? '✓' : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                  <div className="text-xs text-slate-500">Day {s.day} · {s.channel}</div>
                </div>
                {done && <span className="text-xs text-emerald-700 font-medium">Sent</span>}
              </div>
            );
          })}
        </div>
        {stepDone >= steps.length && (
          <div className="mt-3 bg-violet-50 border border-violet-200 rounded-lg p-3 animate-fade-in">
            <div className="text-sm font-bold text-violet-900">{demo.closer}</div>
            <div className="text-xs text-violet-800 mt-1">
              Manual hours saved on this one client: <span className="font-bold">~3.5 hrs/yr</span>. On 200 clients: <span className="font-bold">~700 hrs/yr</span>.
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mt-5">
        <div className="text-sm text-slate-500">
          {stepDone < steps.length ? 'Watching the workflow fire…' : 'Now your cross-sell.'}
        </div>
        <button
          onClick={onNext}
          disabled={stepDone < steps.length}
          className="bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white font-bold px-5 py-2.5 rounded-lg transition-colors"
        >
          Show me #3 →
        </button>
      </div>
    </div>
  );
}

// ── Step 5: Cross-sell demo ──────────────────────────────────
function Step5CrossSellDemo({ firstName, onNext }) {
  const [has, setHas] = React.useState(new Set());

  const toggle = (k) => {
    setHas(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const missing = COVERAGE_OPTIONS.filter(c => !has.has(c.key));
  const oppPerClient = missing.reduce((s, c) => s + (REV_PER_COVERAGE_PER_YR[c.key] || 0), 0);
  const bookOpp = oppPerClient * 200;
  const realisticOpp = Math.round(bookOpp * 0.25);  // 1-in-4 close rate

  return (
    <div className="px-6 py-6">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Hit the checkboxes for what your average client already has</h2>
      <p className="text-slate-600 mb-4">We'll compute what you're leaving on the table. Try it.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {COVERAGE_OPTIONS.map(c => {
          const on = has.has(c.key);
          return (
            <button
              key={c.key}
              onClick={() => toggle(c.key)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                on ? 'bg-emerald-50 border-emerald-400' : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{c.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900">{c.label}</div>
                  <div className="text-[11px] text-slate-500">
                    {on ? '✓ Client has this' : 'Missing'}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className={`rounded-xl p-4 border-2 transition-all ${
        missing.length === 0 ? 'bg-slate-50 border-slate-200' : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300'
      }`}>
        <div className="text-sm font-bold text-emerald-900 mb-1">For a 200-client book:</div>
        <div className="text-4xl font-bold text-emerald-700">
          {missing.length === 0 ? '$0' : `$${realisticOpp.toLocaleString()}`}
        </div>
        <div className="text-xs text-emerald-800 mt-1">
          /year of realistic cross-sell revenue ({missing.length} missing coverages × 200 clients × 25% close rate)
        </div>
        {missing.length > 0 && (
          <div className="text-xs text-emerald-800 mt-2">
            ANCHOR finds every gap automatically. Sends the first nurture text. You only get involved when they say yes.
          </div>
        )}
      </div>

      <button
        onClick={onNext}
        className="w-full mt-5 bg-violet-600 hover:bg-violet-700 text-white font-bold py-3 rounded-lg transition-colors"
      >
        Show me the total →
      </button>
    </div>
  );
}

// ── Step 6: Total value summary ──────────────────────────────
function Step6Total({ firstName, onNext }) {
  return (
    <div className="px-6 py-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-3xl mb-3">💰</div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">{firstName}, here's your annualized math</h2>
        <p className="text-slate-600">Conservative numbers, 200-client book.</p>
      </div>

      <div className="space-y-3">
        <TotalRow icon="📞" label="Hours saved on manual follow-up" value="≈ 168 hrs/yr"   sub="ANCHOR (ACA) + Workflow Engine (Medicare + Life): docs · AOR · first draft · SOA · AEP" />
        <TotalRow icon="📉" label="Lapses prevented (revenue saved)" value="$15,360/yr" sub="ACA grace-period · Medicare AEP retention · Life first-year persistency — all caught" />
        <TotalRow icon="💸" label="Cross-sell revenue captured"     value="$6,000/yr"  sub="ACA → dental/vision/life · Medicare → MedSupp/final expense · Life → spouse/IUL" />
        <TotalRow icon="⚖️" label="Compliance audit risk eliminated" value="Priceless"  sub="Hash-chained immutable audit trail — CMS · TPMO · ASOP · NAIC" hideValue />
      </div>

      <div className="mt-6 bg-gradient-to-br from-violet-100 to-violet-50 border-2 border-violet-300 rounded-xl p-5 text-center">
        <div className="text-sm font-semibold text-violet-900 uppercase tracking-wide">Net to your business</div>
        <div className="text-5xl font-bold text-violet-700 mt-1">$21,360</div>
        <div className="text-xs text-violet-800 mt-1">per year, plus 168 hours back</div>
      </div>

      <button
        onClick={onNext}
        className="w-full mt-5 bg-violet-600 hover:bg-violet-700 text-white font-bold py-3 rounded-lg"
      >
        Show me how to start →
      </button>
    </div>
  );
}

function TotalRow({ icon, label, value, sub, hideValue }) {
  return (
    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-2xl">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 text-sm">{label}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
      <div className={`font-bold ${hideValue ? 'text-slate-500 text-sm italic' : 'text-emerald-700'}`}>{value}</div>
    </div>
  );
}

// ── Step 7: 5-minute setup checklist ─────────────────────────
function Step7Setup({ firstName, onFinish }) {
  return (
    <div className="px-6 py-6">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 text-3xl mb-3">🚀</div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Ready to go, {firstName}?</h2>
        <p className="text-slate-600">Three things to do today (5 min total). Then ANCHOR + the workflow engine take over.</p>
      </div>

      <ol className="space-y-3 mb-6">
        <SetupRow
          n="1" icon="📱" minutes="2 min"
          title="Add your business name + phone"
          detail="The system signs every message with your brand. Settings → Profile & Brand."
        />
        <SetupRow
          n="2" icon="📋" minutes="2 min"
          title="Import your client list"
          detail="CSV upload or paste from any source. We dedupe + match by phone. Settings → Import."
        />
        <SetupRow
          n="3" icon="✅" minutes="1 min"
          title="Mark coverages they already have"
          detail="On any client → toggle dental/vision/life. ANCHOR only pitches what they don't have."
        />
      </ol>

      <button
        onClick={onFinish}
        className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold text-lg py-3 rounded-lg shadow-lg"
      >
        Let's go →
      </button>
      <p className="text-center text-xs text-slate-400 mt-2">You can replay this tour anytime from Settings.</p>
    </div>
  );
}

function SetupRow({ n, icon, minutes, title, detail }) {
  return (
    <li className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg p-3">
      <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">{n}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <div className="font-semibold text-slate-900">{title}</div>
          <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{minutes}</span>
        </div>
        <div className="text-sm text-slate-600 mt-0.5">{detail}</div>
      </div>
    </li>
  );
}

// ── Main exported component ──────────────────────────────────
export default function AnchorValueTour({ currentUser, onComplete = () => {}, onDismiss = () => {} }) {
  const [step, setStep] = React.useState(1);
  const [firstName, setFirstName] = React.useState(currentUser?.email?.split('@')[0]?.replace(/[^A-Za-z]/g, '') || '');
  // Niche steers Step 3 (issue demo) + Step 4 (workflow strip). Defaults
  // to '' so Step 1 gates the Show-me button until the operator picks.
  const [niche, setNiche] = React.useState('');
  const [confirmingSkip, setConfirmingSkip] = React.useState(false);

  // Persist first_name to settings.agent_first_name on completion so
  // the rest of the app can reference it without re-asking. Best-
  // effort — silent if it fails.
  async function persistFirstName() {
    if (!firstName.trim() || !currentUser?.id) return;
    try {
      await supabase.auth.updateUser({ data: { first_name: firstName.trim() } });
    } catch {}
  }

  function next() {
    if (step < TOTAL_STEPS) setStep(s => s + 1);
  }

  async function finish() {
    await persistFirstName();
    markValueTourCompleted();
    onComplete();
  }

  function skip() {
    markValueTourDismissed();
    onDismiss();
  }

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setConfirmingSkip(true); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-violet-600 flex items-center justify-center text-white font-bold text-sm">SFA</div>
            <span className="text-sm font-semibold text-slate-700">Value tour · {step}/{TOTAL_STEPS}</span>
          </div>
          <div className="flex items-center gap-3">
            <ProgressDots step={step} />
            <button
              onClick={() => setConfirmingSkip(true)}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium"
            >
              Skip
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 && <Step1Welcome  firstName={firstName} setFirstName={setFirstName} niche={niche} setNiche={setNiche} onNext={next} />}
          {step === 2 && <Step2Pain     firstName={firstName} onNext={next} />}
          {step === 3 && <Step3AnchorDemo  firstName={firstName} niche={niche} onNext={next} />}
          {step === 4 && <Step4WorkflowDemo firstName={firstName} niche={niche} onNext={next} />}
          {step === 5 && <Step5CrossSellDemo firstName={firstName} onNext={next} />}
          {step === 6 && <Step6Total       firstName={firstName} onNext={next} />}
          {step === 7 && <Step7Setup      firstName={firstName} onFinish={finish} />}
        </div>

        {/* Footer back nav for non-first / non-last */}
        {step > 1 && step < TOTAL_STEPS && (
          <div className="px-6 py-2 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={() => setStep(s => s - 1)}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium"
            >
              ← Back
            </button>
            <span className="text-xs text-slate-400">Press Esc to skip</span>
          </div>
        )}
      </div>

      {/* Skip confirmation */}
      {confirmingSkip && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
            <h3 className="font-bold text-slate-900 mb-1">Skip the tour?</h3>
            <p className="text-sm text-slate-600 mb-4">
              You'll miss the 2-minute walkthrough of how we save you ~$21k/yr and 168 hours. You can replay anytime from Settings.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmingSkip(false)}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white font-semibold py-2 rounded-lg"
              >
                Stay
              </button>
              <button
                onClick={() => { setConfirmingSkip(false); skip(); }}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 rounded-lg"
              >
                Skip anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.35s ease-out; }
      `}</style>
    </div>
  );
}
